import { useEffect, useState } from "react";
import { api, getDepartmentFilter } from "@/lib/api";
import SchoolTrends from "@/components/SchoolTrends";
import MonthlyIncomeChart from "@/components/MonthlyIncomeChart";
import { useI18n } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import ScheduleCoverage from "@/components/ScheduleCoverage";
import FinancialOverview from "@/components/FinancialOverview";
import WallFeed from "@/components/WallFeed";
import PhotosOfWeek from "@/components/PhotosOfWeek";
import LeaderboardCard from "@/components/LeaderboardCard";
import { UnreportedClasses } from "@/components/UnreportedClasses";
import { Send, Loader2, BarChart3, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function SuperadminOverview() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAcademicDir = user?.role === "academic_director";
  const [stats, setStats] = useState({});
  const [running, setRunning] = useState(false);
  const [sendingSummary, setSendingSummary] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  const load = () => api.get("/stats/overview").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runBilling = async () => {
    try {
      setRunning(true);
      const r = await api.post("/admin/run-billing");
      toast.success(`${t("billing_done")}: ${r.data.invoices_processed} ${t("invoices_sent").toLowerCase()} · ${r.data.reminders_sent} ${t("notes").toLowerCase()}`);
      load();
    } catch {
      toast.error(t("error_generic"));
    } finally {
      setRunning(false);
    }
  };

  const sendWeekly = async () => {
    try {
      setSendingSummary(true);
      const r = await api.post("/admin/send-weekly-summary");
      toast.success(`${t("weekly_summary_done")} ${r.data.recipients}`);
    } catch {
      toast.error(t("error_generic"));
    } finally {
      setSendingSummary(false);
    }
  };

  const sendDailyReminders = async () => {
    try {
      setSendingReminders(true);
      const r = await api.post("/admin/send-daily-reminders");
      toast.success(`${t("daily_reminders_done")}: ${r.data.sent} · ${r.data.skipped_already_sent} ya enviados`);
    } catch {
      toast.error(t("error_generic"));
    } finally {
      setSendingReminders(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t("summary")}
        subtitle={t("tagline")}
        testId="overview-header"
        action={isAcademicDir ? null : (
          <div className="flex gap-2 flex-wrap">
            <Button
              data-testid="daily-reminders-button"
              onClick={sendDailyReminders}
              disabled={sendingReminders}
              variant="outline"
            >
              {sendingReminders ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />}
              {t("daily_reminders")}
            </Button>
            <Button
              data-testid="weekly-summary-button"
              onClick={sendWeekly}
              disabled={sendingSummary}
              variant="outline"
            >
              {sendingSummary ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4" />}
              {t("weekly_summary")}
            </Button>
            <Button
              data-testid="run-billing-button"
              onClick={runBilling}
              disabled={running}
              className="btn-brand"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {t("run_billing")}
            </Button>
          </div>
        )}
      />
      {!isAcademicDir && (
        <p className="text-xs text-slate-500 mb-5 -mt-3">{t("daily_reminders_desc")}</p>
      )}
      <SchoolTrends />
      <UnreportedClasses />
      {stats.test_accounts > 0 && (
        <p className="mt-3 text-[11.5px] text-slate-400" data-testid="test-accounts-note">
          {stats.test_accounts} {t("test_accounts_excluded")}
        </p>
      )}

      {isAcademicDir ? <ScheduleCoverage /> : <FinancialOverview />}

      {/* Tendencia de ingresos: solo en la vista consolidada, en dólares. */}
      {!isAcademicDir && getDepartmentFilter() === "" && <MonthlyIncomeChart />}

      {/* Ranking, fotos y muro son de We Speak Spanish: en Inglés y en la vista
          consolidada el resumen queda solo con los datos. */}
      {getDepartmentFilter() === "dep_es" && (
        <div className="space-y-8 mt-8" data-testid="admin-wall-section">
          <LeaderboardCard />
          <PhotosOfWeek />
          <WallFeed limit={5} />
        </div>
      )}
    </div>
  );
}
